/**
 * @file 「さいきん書いたメモ」の並びを組む純関数である。
 *
 * @what: 書き足した文の記録 (StudyNoteEntry) を「日 × 本」でまとめ、新しい日から limit 件を返す。
 *   同じ本に同じ日いくつメモを書いても1件にし、文はつなげる。別冊は本体の本にまとめる。記録を
 *   持たない古いメモ (器を足す前に書いたもの) は、更新日の1日だけ、いまの本文の冒頭で補う。
 * @why: 本だなの右に、本の表紙とその日に書いた文を新しい順に並べる (代表の要望、2026-09-13)。
 *   出すのはメモに文を書いた日だけで、開いただけの日は出さない。
 * @failure: 副作用を持たない。本だなに無い資料 id は表紙なしのまま並べる (記録は消さない)。
 */

import type { StudyNoteEntry } from "../api/noteEntries";
import type { StudyNote } from "../api/notes";
import type { StudySource } from "../api/sources";
import { referencedNoteImageIds, stripNoteImageRefs } from "./noteImages";

/** 出す件数の上限である。 */
export const RECENT_NOTE_FEED_LIMIT = 20;

/** 記録を読む期間 (日) である。API の上限 (400日) の中に収める。 */
export const RECENT_NOTE_FEED_DAYS = 180;

/** 1件に出す文の行数の上限である。 */
export const RECENT_NOTE_FEED_LINES = 3;

export interface RecentNoteFeedItem {
  key: string;
  date: string;
  /** まとめた本 (別冊なら本体) の資料 id で、本に紐づかないメモは空である。 */
  bookId: string;
  /** その日に書いたメモの id (新しい順)。 */
  noteIds: string[];
  /** 先頭のメモの題である。 */
  title: string;
  /** その日に書いた文 (先頭から RECENT_NOTE_FEED_LINES 行) である。 */
  lines: string[];
  /** その日に足した画像の数である。 */
  images: number;
  /** 最後に書いた時刻 (ISO) で、並べ替えに使う。 */
  lastAt: string;
  /** 開くページ (リーダーの連番) で、無ければ資料の先頭を開く。 */
  page?: number;
}

/** 本文から出す行を選ぶ (空行と画像の参照だけの行は飛ばし、見出しの印を落とす)。 */
export function feedLines(text: string, limit: number = RECENT_NOTE_FEED_LINES): string[] {
  const out: string[] = [];
  for (const raw of text.split("\n")) {
    const stripped = stripNoteImageRefs(raw).trim();
    if (stripped === "") continue;
    out.push(stripped.replace(/^#{1,6}\s+/, ""));
    if (out.length >= limit) break;
  }
  return out;
}

function localDateOf(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function bookOf(sourceId: string | undefined, sourcesById: ReadonlyMap<string, StudySource>): string {
  if (!sourceId) return "";
  const s = sourcesById.get(sourceId);
  return s?.companion_of ?? sourceId;
}

interface Bucket {
  date: string;
  bookId: string;
  notes: { id: string; title: string; text: string; images: number; lastAt: string }[];
}

export function buildRecentNoteFeed(input: {
  entries: readonly StudyNoteEntry[];
  notes: readonly StudyNote[];
  sourcesById: ReadonlyMap<string, StudySource>;
  /** 記録を読んだ期間の初日で、これより前の古いメモは補わない。 */
  from: string;
  limit?: number;
}): RecentNoteFeedItem[] {
  const { entries, notes, sourcesById, from, limit = RECENT_NOTE_FEED_LIMIT } = input;
  const buckets = new Map<string, Bucket>();
  const seenNoteDays = new Set<string>();
  const put = (
    date: string,
    bookId: string,
    note: { id: string; title: string; text: string; images: number; lastAt: string },
  ) => {
    const key = `${date}:${bookId}`;
    const bucket = buckets.get(key) ?? { date, bookId, notes: [] };
    bucket.notes.push(note);
    buckets.set(key, bucket);
    seenNoteDays.add(`${date}:${note.id}`);
  };

  for (const e of entries) {
    put(e.date, bookOf(e.source_ids[0], sourcesById), {
      id: e.note_id,
      title: e.title,
      text: e.text,
      images: e.images,
      lastAt: e.last_at,
    });
  }
  // 記録を持たない古いメモは、更新日の1日だけ、いまの本文で補う
  for (const n of notes) {
    const date = localDateOf(n.updated_at);
    if (date === null || date < from) continue;
    if (seenNoteDays.has(`${date}:${n.id}`)) continue;
    const body = n.body ?? "";
    const images = n.images?.length ?? referencedNoteImageIds(body).size;
    if (feedLines(body, 1).length === 0 && images === 0) continue;
    const firstSource = (n.refs ?? []).find((r) => r.kind === "source")?.source_id;
    put(date, bookOf(firstSource, sourcesById), {
      id: n.id,
      title: n.title,
      text: body,
      images,
      lastAt: n.updated_at,
    });
  }

  const notesById = new Map(notes.map((n) => [n.id, n]));
  const items: RecentNoteFeedItem[] = [];
  for (const b of buckets.values()) {
    b.notes.sort((x, y) => (x.lastAt < y.lastAt ? 1 : x.lastAt > y.lastAt ? -1 : 0));
    const first = b.notes[0]!;
    const lines = feedLines(b.notes.map((n) => n.text).join("\n"));
    const images = b.notes.reduce((sum, n) => sum + n.images, 0);
    if (lines.length === 0 && images === 0) continue;
    // 開くページは、先頭のメモがその本に付けている最初のページ (無ければ資料の先頭)
    const note = notesById.get(first.id);
    const ref = (note?.refs ?? []).find(
      (r) => r.kind === "source" && bookOf(r.source_id, sourcesById) === b.bookId,
    );
    items.push({
      key: `${b.date}:${b.bookId}`,
      date: b.date,
      bookId: b.bookId,
      noteIds: b.notes.map((n) => n.id),
      title: first.title,
      lines,
      images,
      lastAt: first.lastAt,
      ...(ref?.page ? { page: ref.page } : {}),
    });
  }
  items.sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;
    return a.lastAt < b.lastAt ? 1 : a.lastAt > b.lastAt ? -1 : 0;
  });
  return items.slice(0, limit);
}
